import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BankAccount } from '@payments/models/payment-preferences-page-view-model';
import { PaymentPreferenceSortFields } from '@payments/pages/payment-preferences/services/payment-preferences.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { SortDirection, SortFilter } from '@usf/ngrx-payment';

@Component({
  selector: 'app-bank-accounts-to-deduct',
  templateUrl: './bank-accounts-to-deduct.component.html',
  styleUrls: ['./bank-accounts-to-deduct.component.scss'],
})
export class BankAccountsToDeductComponent {
  @Input() bankAccounts: BankAccount[];
  @Input() isAutopay: boolean;
  @Input() isAutopayEnabled: boolean;
  @Input() isDesktop: boolean;
  @Input() isMobile: boolean;
  @Input() canCollapseAccordion: boolean;
  @Input() canRemove: boolean;
  @Input() sortFilter: SortFilter;
  @Input() isEligleAutopayCustomers = true;

  @Output() actionButtonEmitter: EventEmitter<boolean> = new EventEmitter();
  @Output() editButtonEmit: EventEmitter<BankAccount> = new EventEmitter();
  @Output() removeButtonEmit: EventEmitter<BankAccount> = new EventEmitter();
  @Output() sortFieldEmit: EventEmitter<SortFilter> = new EventEmitter();

  readonly SortDirection = SortDirection;
  readonly SortFields = PaymentPreferenceSortFields;

  constructor(
    private actionSheetController: ActionSheetController,
    private popoverCtrl: PopoverController,
    private translateService: TranslateService,
  ) {}

  onClickBtn = (event?: Event) => {
    event?.stopPropagation();
    this.actionButtonEmitter.emit(this.isAutopayEnabled);
  };

  handleSort({
    sortField,
    sortDirection,
  }: {
    sortField: string;
    sortDirection: SortDirection;
  }) {
    this.sort(sortField, sortDirection);
  }

  sort(field: string, direction: SortDirection = SortDirection.DESC) {
    if (field === this.sortFilter.fieldName) {
      this.sortFilter.sortValue =
        this.sortFilter.sortValue === SortDirection.ASC
          ? SortDirection.DESC
          : SortDirection.ASC;
    } else {
      this.sortFilter.sortValue = direction;
    }
    this.sortFilter.fieldName = field;

    this.sortFieldEmit.emit(this.sortFilter);
  }

  getSectionName(useDash: boolean = false): string {
    if (this.isAutopay) {
      return 'autopay';
    }
    return useDash ? 'bank-accounts' : 'bankAccounts';
  }

  editBankAccount(bankDetail: BankAccount) {
    this.editButtonEmit.emit(bankDetail);
  }

  removeBankAccount(bankDetail: BankAccount) {
    this.removeButtonEmit.emit(bankDetail);
  }

  async openMoreOptions(optionsEvent: {
    event: any;
    bankAccount: BankAccount;
  }) {
    const buttons: any[] = [
      {
        text: this.translateService.instant(
          this.canRemove
            ? 'i18n.common.edit'
            : 'i18n.payments.paymentPreferences.editViewOnly',
        ),
        handler: () => this.editBankAccount(optionsEvent.bankAccount),
      },
      {
        text: this.translateService.instant(
          this.canRemove
            ? 'i18n.payments.paymentPreferences.remove'
            : 'i18n.payments.paymentPreferences.removeViewOnly',
        ),
        cssClass: !this.canRemove ? 'remove-button-disable' : '',
        handler: () =>
          this.canRemove && this.removeBankAccount(optionsEvent.bankAccount),
      },
    ];

    if (!this.isDesktop) {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
        handler: () => {},
      });

      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await actionSheet.present();
    } else {
      const listOptionsPopover = await this.popoverCtrl.create({
        dismissOnSelect: true,
        component: EllipsisOptionsPopoverComponent,
        event: optionsEvent.event,
        componentProps: {
          buttons,
        },
        cssClass: 'ellipsis-options-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });

      await listOptionsPopover.present();
    }
  }
}
